import { calendarEventsLockedStore } from '../store/lockedCalendarStore';
import { lazyDeleteCalendarEventWithConfirm } from 'owa-calendar-event-actions-with-confirm';
import { isSeriesMaster } from 'owa-calendar-event-capabilities';
import { lazyOpenNotesEditor } from 'owa-calendar-notes-editor';
import type CalendarTime from 'owa-calendar-types/lib/types/CalendarTime';
import { CalendarComponent } from 'owa-calendar-usage-instrumentation';
import { ControlIcons } from 'owa-control-icons';
import { launchCalendarFullComposeToDuplicate } from 'owa-calendar-forms-launch-handlers';
import { lazyPopoutCalendarReadingPane } from 'owa-popout-calendar';
import EventScope from 'owa-service/lib/contract/EventScope';
import { orchestrator } from 'satcheljs';
import { cancelCalendarEvent, respondToCalendarEvent } from 'owa-calendar-events-actions';
import {
    getInitializedEventId,
    getInitializedEvent,
    isEventInitialized,
    getSourceId,
} from '../selectors/calendarEventContextMenuStoreSelectors';
import {
    cancelMeeting,
    deleteEvent,
    fetchEventData,
    forwardEvent,
    proposeNewTime,
    replyToMeeting,
    respondToMeeting,
    updateInitializedEventId,
    verifyEventData,
    openEventDetails,
    openEventContextMenu,
    duplicateEvent,
} from '../actions/calendarEventContextMenuStoreActions';
import EditorScenarios from 'owa-editor/lib/store/schema/EditorScenarios';
import type { FullComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { forwardSurfaceEvent } from 'owa-calendar-surface-events-common';
import { replyToMeeting as replyToMeetingFunc } from 'owa-reply-to-meeting';

export const openEventContextMenuOrchestrator = orchestrator(
    openEventContextMenu,
    actionMessage => {
        const { viewState } = actionMessage;
        if (!viewState || !viewState.itemId) {
            updateInitializedEventId(null);
        } else {
            fetchEventData(viewState.itemId);
        }
    }
);

export const fetchEventDataOrchestrator = orchestrator(fetchEventData, async actionMessage => {
    const { itemId } = actionMessage;

    if (!itemId || itemId === getInitializedEventId()) {
        return;
    }

    updateInitializedEventId(null);
    const { fullEventPromise } = calendarEventsLockedStore.fetchFullCalendarEvent(
        itemId,
        undefined /** folderId */,
        undefined /** getExistingCalendarEvent */,
        undefined /** eventScope */,
        { fetchSource: getSourceId() } /** customData */
    );
    const event = await fullEventPromise;
    if (event) {
        updateInitializedEventId(itemId);
    }
});

export const verifyEventDataOrchestrator = orchestrator(verifyEventData, actionMessage => {
    const { handleItemRemoved } = actionMessage;
    if (isEventInitialized() && !getInitializedEvent()) {
        handleItemRemoved?.();
    }
});

export const replyToMeetingOrchestrator = orchestrator(replyToMeeting, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    replyToMeetingFunc(calendarItem, actionMessage.isReplyAll, getSourceId());
});

export const forwardEventOrchestrator = orchestrator(forwardEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    forwardSurfaceEvent(calendarItem, actionMessage.eventScope, openEventDetails);
});

export const respondToMeetingOrchestrator = orchestrator(respondToMeeting, actionMessage => {
    const { newResponseType, eventScope, editorTitleText } = actionMessage;

    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    const sourceId = getSourceId();

    if (calendarItem.IsResponseRequested) {
        const onSend = (
            eventScope: EventScope,
            notes: string,
            proposeNewTime: CalendarTime,
            sendResponse: boolean
        ) => {
            respondToCalendarEvent(
                calendarItem.ItemId,
                calendarItem.ResponseType,
                newResponseType,
                calendarItem.FreeBusyType,
                eventScope,
                sendResponse,
                sourceId,
                notes,
                proposeNewTime,
                false /* Should show declined events */
            );
        };
        lazyOpenNotesEditor.importAndExecute(calendarItem.ItemId.Id, {
            editorScenario: EditorScenarios.ItemPeekToRespond,
            eventScope: eventScope,
            onSend: onSend,
            titleText: editorTitleText,
            iconName: ControlIcons.CalendarReply,
            showNotifyToggle: true,
            sendResponse: true,
            responseType: newResponseType,
        });
        openEventDetails(calendarItem.ItemId);
    } else {
        respondToCalendarEvent(
            calendarItem.ItemId,
            calendarItem.ResponseType,
            newResponseType,
            calendarItem.FreeBusyType,
            eventScope,
            false /* Send Response */,
            sourceId
        );
    }
});

export const proposeNewTimeOrchestrator = orchestrator(proposeNewTime, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyPopoutCalendarReadingPane.importAndExecute(calendarItem.ItemId, getSourceId(), {
        responseType: actionMessage.responseType,
    });
});

export const deleteEventOrchestrator = orchestrator(deleteEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyDeleteCalendarEventWithConfirm.importAndExecute(
        calendarItem,
        actionMessage.eventScope,
        isSeriesMaster(calendarItem),
        getSourceId(),
        false /*isInPopOutWindow*/,
        actionMessage.disableConfirmationDialog
    );
});

export const cancelMeetingOrchestrator = orchestrator(cancelMeeting, actionMessage => {
    const { editorTitleText, eventScope } = actionMessage;
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyOpenNotesEditor.importAndExecute(calendarItem.ItemId.Id, {
        editorScenario: EditorScenarios.ItemPeekToRespond,
        eventScope: eventScope,
        onSend: (eventScope: EventScope, notes?: string) => {
            cancelCalendarEvent(
                calendarItem.ItemId,
                getSourceId(),
                eventScope,
                CalendarComponent.CalendarEventContextMenu,
                notes
            );
        },
        titleText: editorTitleText,
        iconName: ControlIcons.RemoveEvent,
        showNotifyToggle: false,
        sendResponse: true,
        isCancelledEvent: true,
        responseType: 'Cancel',
    });
    openEventDetails(calendarItem.ItemId);
});

export const duplicateEventOrchestrator = orchestrator(duplicateEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    const entrySource = (getSourceId() + 'Duplicate') as FullComposeEntrySourceType;

    launchCalendarFullComposeToDuplicate(calendarItem.ItemId, entrySource);
});
